import type { TFunction } from 'i18next';

import type { AvailabilityDto, DateTimeUnixType, DigitalAdPressure, PostCampaignDto } from '@/services/ws-post/src/main/resources/rest-api';
import {
  AdChannelTypeEnum,
  AddressProvider,
  OnlineDeviceDto,
  OnlineFormatDto,
  OnlineInventoryDto,
  fromDate,
  fromDateTime,
  toDate,
  toDateTime,
} from '@/services/ws-post/src/main/resources/rest-api';
import type { DateType } from '@/js/libs/api-helpers';
import type { Dayjs } from '@/js/libs/dayjs';
import dayjs from '@/js/libs/dayjs';

import { HOLIDAYS, REACH_BY_DELIVERY_FACTOR } from './constants';
import type { DeliveriesByLanguageMap } from './types';
import { DiscountPositionName, PostPositionType } from './types';

export const getAdPressureLabel = (adPressure: DigitalAdPressure, t: TFunction): string =>
  `${t(adPressure.strength)} - ${adPressure.loopLength} Min`;

export const getTotalMailingDeliveries = (deliveries: DeliveriesByLanguageMap): number =>
  Object.values(deliveries).reduce((acc, languageDeliveries) => acc + languageDeliveries, 0);

export const getMailingPositionReach = (deliveries: DeliveriesByLanguageMap): number => {
  const totalDeliveries = getTotalMailingDeliveries(deliveries);
  const reach = Math.round(totalDeliveries * REACH_BY_DELIVERY_FACTOR);

  return reach;
};

export function getPostDtoDate(value: DateTimeUnixType): DateType;
export function getPostDtoDate(value: DateTimeUnixType | undefined): DateType | undefined;
export function getPostDtoDate(value: DateTimeUnixType | null): DateType | undefined;
export function getPostDtoDate(value: DateTimeUnixType | null | undefined): DateType | undefined {
  if (!value) {
    return undefined;
  }

  return toDate(fromDateTime(value));
}

export const getMinEndDate = (startDate: Date | null | undefined): Date | undefined => dayjs(startDate)?.toDate();

export const getPostDateValue = (value: DateTimeUnixType | null | undefined): Dayjs | undefined => {
  const date = fromDateTime(value);

  return date?.isValid() ? date : undefined;
};

export function getPostDateTime(date: DateType): DateTimeUnixType;
export function getPostDateTime(date: DateType | undefined): DateTimeUnixType | undefined;
export function getPostDateTime(date: DateType | undefined): DateTimeUnixType | undefined {
  return toDateTime(fromDate(date));
}

export const validatedDistributionDate = (date: DateTimeUnixType | null | undefined, t: TFunction): string | undefined => {
  if (!date) {
    return t('messages:DISTRIBUTION_DATE_REQUIRED');
  }
  return undefined;
};

const holidays = HOLIDAYS.map((dateStr) => dayjs(dateStr, 'MM-DD-YYYY'));
export const filterHolidayDays = (date: Date) => {
  const day = date.getDay();
  if (day === 0) {
    return false;
  }

  if (holidays.find((holiday) => holiday.isSame(dayjs(date)))) {
    return false;
  }

  return true;
};

export const isWeekday = (date: Date) => {
  const day = date.getDay();
  return day !== 0 && day !== 6;
};

export const hasPositions = (postCampaign: PostCampaignDto | null): boolean => {
  return Boolean(
    postCampaign?.hasDigitalPositions ||
      postCampaign?.hasMailingPositions ||
      postCampaign?.hasServicePositions ||
      postCampaign?.hasDiscountPositions ||
      postCampaign?.hasEmailPositions ||
      postCampaign?.hasAddressedMailingPositions ||
      postCampaign?.hasOnlinePositions,
  );
};

export const checkAdUnitsImported = (postCampaign: PostCampaignDto | null): boolean => {
  if (!postCampaign) {
    return false;
  }

  const { adUnitsImported } = postCampaign;

  return adUnitsImported;
};

export const getProviderTitleMap = (t: TFunction): Record<AddressProvider, string> => ({
  [AddressProvider.AZ]: t('postCampaign:ADDRESS_PROVIDER_AZ'),
  [AddressProvider.KUBA]: t('postCampaign:ADDRESS_PROVIDER_KUBA'),
  [AddressProvider.POST]: t('postCampaign:ADDRESS_PROVIDER_POST'),
});

export const getUnavailablePositionType = (availabilityErrors: AvailabilityDto[]): PostPositionType | null => {
  const [firstError] = availabilityErrors.filter((error) => !error.isAvailable);

  switch (firstError?.type) {
    case AdChannelTypeEnum.PHYSICAL_ADDRESSED_MAILING:
      return PostPositionType.AddressedMailing;
    case AdChannelTypeEnum.EMAIL:
      return PostPositionType.Email;
    default:
      return null;
  }
};

export const getChannelLabelMap = (t: TFunction): Record<AdChannelTypeEnum, string> => ({
  [AdChannelTypeEnum.DOOH_SCREEN_ZONE]: t('postCampaign:DOOH_CHANNEL'),
  [AdChannelTypeEnum.EMAIL]: t('postCampaign:EMAIL_CHANNEL'),
  [AdChannelTypeEnum.PHYSICAL_ADDRESSED_MAILING]: t('postCampaign:ADDRESSED_MAILING_CHANNEL'),
  [AdChannelTypeEnum.POSTCODE]: t('postCampaign:UNADDRESSED_MAILING_CHANNEL'),
  [AdChannelTypeEnum.ONLINE]: t('postCampaign:ONLINE_CHANNEL'),
});

export const getChannelReachLabelMap = (t: TFunction): Record<AdChannelTypeEnum, string> => ({
  [AdChannelTypeEnum.DOOH_SCREEN_ZONE]: t('postCampaign:REACH_PER_CHANNEL_DOOH'),
  [AdChannelTypeEnum.EMAIL]: t('postCampaign:REACH_PER_CHANNEL_EMAIL'),
  [AdChannelTypeEnum.PHYSICAL_ADDRESSED_MAILING]: t('postCampaign:REACH_PER_CHANNEL_ADDRESSED_MAILING'),
  [AdChannelTypeEnum.POSTCODE]: t('postCampaign:REACH_PER_CHANNEL_UNADDRESSED_MAILING'),
  [AdChannelTypeEnum.ONLINE]: t('postCampaign:REACH_PER_CHANNEL_ONLINE'),
});

export const getDiscountPositionLabel = (name: DiscountPositionName, t: TFunction): string => {
  switch (name) {
    case DiscountPositionName.DiscountPosition:
      return t('postCampaign:DISCOUNT_POSITION');
    default:
      throw new Error('Invalid discount position name');
  }
};

export const getOnlineDeviceLabelMap = (t: TFunction): Record<OnlineDeviceDto, string> => ({
  [OnlineDeviceDto.DESKTOP]: t('postBookingTool:DESKTOP'),
  [OnlineDeviceDto.MOBILE_INAPP]: t('postBookingTool:MOBILE_INAPP'),
  [OnlineDeviceDto.MOBILE_WEB]: t('postBookingTool:MOBILE_WEB'),
});

export const getOnlineFormatLabelMap = (t: TFunction): Record<OnlineFormatDto, string> => ({
  [OnlineFormatDto.DISPLAY]: t('postBookingTool:DISPLAY'),
  [OnlineFormatDto.INTERSTITIAL]: t('postBookingTool:INTERSTITIAL'),
  [OnlineFormatDto.INTERSTITIALVIDEOUNSKIPPABLE]: t('postBookingTool:INTERSTITIALVIDEOUNSKIPPABLE'),
  [OnlineFormatDto.INTERSTITIALVIDEOSKIPPABLE]: t('postBookingTool:INTERSTITIALVIDEOSKIPPABLE'),
});

export const getOnlineInventoryLabelMap = (t: TFunction): Record<OnlineInventoryDto, string> => ({
  [OnlineInventoryDto.PCC]: t('postCampaign:INVENTORY_PCC'),
  [OnlineInventoryDto.POSTRON]: t('postCampaign:INVENTORY_POSTRON'),
});
